import { clone } from "lodash";
import {
  fetchTriggerDetail,
  fetchTriggerFieldOptions,
  updateTrigger,
  createTrigger,
} from "../services.js";
import {
  getConfigKeys,
  getNextKey,
  mapMultiSelectValue,
  mapMultiSelectOptions,
  getCombinationFromOperator,
  mapMultiselectGroupedValue,
  mapMultiselectGroupedOptions,
} from "../utils.js";
import {
  TRIGGER_FIELDS,
  TRIGGER_FIELDS_LIST,
  FIELD_KEYS,
  PAYLOAD_MAP,
  TRIGGER_FIELDS_ENDPOINTS,
  PAYLOAD_TO_COMPONENT_TYPES_MAP,
  PAYLOAD_TO_COMPONENT_MODELS_MAP,
  PAYLOAD_COMPONENT_TYPES,
  DIVIDER,
  COMPONENT_TYPES,
} from "../constants.js";

export default {
  methods: {
    async fetchInitialData() {
      try {
        if (!this.isViewMode) {
          const { Events, Conditions, Actions } = TRIGGER_FIELDS;
          const { Type } = FIELD_KEYS;

          const [events, conditions, actions] = await Promise.allSettled([
            this.fetchOptions({ endpoint: TRIGGER_FIELDS_ENDPOINTS[Events] }),
            this.fetchOptions({ endpoint: TRIGGER_FIELDS_ENDPOINTS[Conditions] }),
            this.fetchOptions({ endpoint: TRIGGER_FIELDS_ENDPOINTS[Actions] })
          ]);

          this[Events].options[Type] = events?.value ?? [];
          this[Conditions].blocks[0].options[0][Type] = conditions?.value?.condition_list ?? [];
          this[Conditions].blocks[0].components[0][Type] = PAYLOAD_TO_COMPONENT_TYPES_MAP[conditions?.value?.field_type];
          this[Actions].options[0][Type] = actions?.value?.action_list ?? [];
          this[Actions].components[0][Type] =  PAYLOAD_TO_COMPONENT_TYPES_MAP[actions?.value?.field_type];
        }

        if (this.getTriggerId) {
          await this.getTriggerDetail();
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoaderShown = false;
      }
    },

    async getTriggerDetail() {
      try {
        const data = await fetchTriggerDetail({
          type: this.getType,
          workflow_id: this.getWorkflowId,
          trigger_id: this.getTriggerId
        });

        await this.initModel(data);
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoaderShown = false;
      }
    },

    async initModel(triggerDetail = {}) {
      for (const field in triggerDetail) {
        if (TRIGGER_FIELDS_LIST.includes(field)) {
          switch (field) {
            case TRIGGER_FIELDS.Events: {
              await this.initEvent(triggerDetail, field);
              break;
            }

            case TRIGGER_FIELDS.ConditionBlocks: {
              await this.initConditions(triggerDetail, field);
              break;
            }

            case TRIGGER_FIELDS.Actions: {
              await this.initActions(triggerDetail, field);
              break;
            }

            default:
              break;
          }
        }
      }

      this.details.model.name = triggerDetail.name;
      this.details.model.description = triggerDetail.description ?? "";
    },

    async initEvent(triggerDetail, field) {
      const configModelKeys = PAYLOAD_MAP.get(field);
      const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

      for (const [idx, mk] of configModelKeys.entries()) {
        const item = triggerDetail[field][idx];
        if (!item) continue;

        if (this.isViewMode) this[field].options[mk] = [item];

        const combinedKey = [item[idKey], item[configIdKey], item[configValueKey]].join(DIVIDER);
        await this.handleObjectInput(combinedKey, field, mk);
        this[field].model[mk] = combinedKey;
      }
    },

    async initConditions(triggerDetail, field) {
      const configModelKeys = PAYLOAD_MAP.get(field);
      const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

      for (const [blockIndex, block] of triggerDetail[field].entries()) {
        const resolvedField = TRIGGER_FIELDS.Conditions;
        if (blockIndex > 0) this.addBlock(resolvedField);

        for (const [fieldsSetIndex, fieldsSetArr] of block.conditions.entries()) {
          if (fieldsSetIndex > 0) this.addItem(resolvedField, blockIndex);

          for (const [idx, mk] of configModelKeys.entries()) {
            let item = fieldsSetArr[idx];
            if (!item) continue;

            switch (item.field_type) {
              case PAYLOAD_COMPONENT_TYPES.MultiSelect: {
                if (this.isViewMode) {
                  this[resolvedField].blocks[blockIndex].components[fieldsSetIndex][mk] = PAYLOAD_TO_COMPONENT_TYPES_MAP[item.field_type];
                  this[resolvedField].blocks[blockIndex].options[fieldsSetIndex][mk] = mapMultiSelectOptions(item, field);
                }

                this[resolvedField].blocks[blockIndex].model[fieldsSetIndex][mk] = mapMultiSelectValue(item, field);
                break;
              }

              case PAYLOAD_COMPONENT_TYPES.Range: {
                this[resolvedField].blocks[blockIndex].components[fieldsSetIndex][mk] = PAYLOAD_TO_COMPONENT_TYPES_MAP[item.field_type];
                this[resolvedField].blocks[blockIndex].model[fieldsSetIndex][mk] = {
                  [idKey]: item[idKey],
                  range: item[configValueKey].split("-"),
                }
                break;
              }

              default: {
                if (this.isViewMode) {
                  this[resolvedField].blocks[blockIndex].options[fieldsSetIndex][mk] = [item];
                }

                const combinedKey = [item[idKey], item[configIdKey], item[configValueKey]].join(DIVIDER);
                await this.handleArrayInput(combinedKey, fieldsSetIndex, resolvedField, mk, blockIndex);
                this[resolvedField].blocks[blockIndex].model[fieldsSetIndex][mk] = combinedKey;
              }
            }

            this[resolvedField].blocks[blockIndex].operator = getCombinationFromOperator(block.conditions_operator);
          }
        }
      }

      const { condition_blocks_operator } = triggerDetail;
      if (condition_blocks_operator?.length > 0) {
        this.conditions.operator.main = condition_blocks_operator
      }
    },

    async initActions(triggerDetail, field) {
      const configModelKeys = PAYLOAD_MAP.get(field);
      const { idKey, configIdKey, configValueKey } = getConfigKeys(field);

      for (const [fieldsSetIndex, fieldsSetObj] of triggerDetail[field].entries()) {
        if (fieldsSetIndex > 0) this.addItem(field);

        const { unit, value } = fieldsSetObj?.action_delay_config ?? {};
        if (value) {
          this[field].delay[fieldsSetIndex].unit = unit;
          this[field].delay[fieldsSetIndex].value = String(value);
          this[field].delay[fieldsSetIndex].isDelayed = value > 0;
        }

        for (const [idx, mk] of configModelKeys.entries()) {
          const item = fieldsSetObj.actions[idx];
          if (!item) continue;

          switch (item.field_type) {
            case PAYLOAD_COMPONENT_TYPES.Email: {
              this[field].components[fieldsSetIndex][mk] = PAYLOAD_TO_COMPONENT_TYPES_MAP[item.field_type];
              this[field].model[fieldsSetIndex][mk] = {
                [idKey]: item[idKey],
                text: item[configValueKey],
              }

              if (this.isViewMode) {
                this[field].options[fieldsSetIndex][mk] = [{
                  [idKey]: item[idKey],
                  [configIdKey]: item[configIdKey],
                  [configValueKey]: item[configValueKey],
                  field_type: item.field_type,
                }]
              }
              break;
            }

            case PAYLOAD_COMPONENT_TYPES.MultiSelectGrouped: {
              if (this.isViewMode) {
                this[field].components[fieldsSetIndex][mk] = PAYLOAD_TO_COMPONENT_TYPES_MAP[item.field_type];
                this[field].options[fieldsSetIndex][mk] = mapMultiselectGroupedOptions(item, field)
              }

              this[field].model[fieldsSetIndex][mk] = mapMultiselectGroupedValue(item, field);
              break;
            }

            default: {
              if (this.isViewMode) this[field].options[fieldsSetIndex][mk] = [item];
              const combinedKey = [item[idKey], item[configIdKey], item[configValueKey]].join(DIVIDER);
              await this.handleArrayInput(combinedKey, fieldsSetIndex, field, mk);
              this[field].model[fieldsSetIndex][mk] = combinedKey;
            }
          }
        }
      }
    },

    async fetchOptions({ endpoint }, params) {
      try {
        const data = await fetchTriggerFieldOptions({
          type: this.getType,
          workflow_id: this.getWorkflowId,
          endpoint,
          params,
          trigger_id: this.getTriggerId,
        });

        return data;
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    async handleSubmit() {
      let payload = {
        workflow_id: this.getWorkflowId,
        name: this.details.model.name.trim(),
        ...(this.isDescriptionIncluded ? { description: this.details.model.description.trim() } : {} ),
        condition_blocks_operator: this.isConditionsPartiallyFilled ? this.conditions.operator.main : null,
        events: this.getPayloadForEvents(),
        condition_blocks: this.isConditionsPartiallyFilled
          ? this.getPayloadForConditions()
          : [],
        actions: this.getPayloadForActions(),
      };

      try {
        this.isLoading = true;

        if (this.getTriggerId) {
          await updateTrigger({ type: this.getType, trigger_id: this.getTriggerId, payload });
        } else {
          await createTrigger({ type: this.getType, payload });
        }

        this.$toast.success(this.submitMessage);

        return this.$router.push({
          name: "Workflow Tools",
          params: {
            workflowTool: "triggerAndActions",
            workflowId: this.getWorkflowId,
          },
        });
      } catch (err) {
        console.error(err.response.data.status_message);
        this.$toast.error(err.response.data.status_message);
      } finally {
        this.isLoading = false;
      }
    },

    async handleObjectInput(combinedValue, field, currentKey) {
      try {
        if (!field && !currentKey) return;

        const nextKey = getNextKey(field, currentKey);
        if (!nextKey) return;
        if (this.isViewMode) return;

        const { idKey, configIdKey, endpoint } = getConfigKeys(field);
        const item = this.getObjectItemFromOptions(combinedValue, field, currentKey);

        this.isLoading = true;

        if (item?.has_child) {
          const memoOpts = this[field].memo[combinedValue];

          if (!memoOpts?.length) {
            const result = await this.fetchOptions({ endpoint }, {
              [idKey]: item[idKey],
              [configIdKey]: item[configIdKey]
            });
            this[field].options[nextKey] = result;
            this.$set(this[field].memo, combinedValue, result);
          } else {
            this[field].options[nextKey] = this[field].memo[combinedValue];
          }
        }

        this.resetChildModel(field, currentKey);
      } catch (err) {
        console.error("err", err);
      } finally {
        this.isLoading = false;
      }
    },

    async handleArrayInput(combinedValue, index, field, currentKey, blockIndex) {
      try {
        const nextKey = getNextKey(field, currentKey);
        const hasNoChild = this.checkIsConditionsField(field)
          && this[field].blocks?.[blockIndex]?.components?.[index]?.[currentKey] !== this.selectComponentType;
        if (!nextKey || hasNoChild || this.isViewMode) return;
        if (this[field].components?.[index]?.[currentKey] === COMPONENT_TYPES.MultiSelectGrouped) return;

        const { idKey, configIdKey, endpoint } = getConfigKeys(field);
        const item = this.getArrayItemFromOptions(combinedValue, field, currentKey, index, blockIndex);

        this.isLoading = true;

        this.resetChildModel(field, currentKey, index, blockIndex);

        if (item?.has_child) {
          const memoOpts = this[field].memo[combinedValue];

          if (!memoOpts?.length) {
            const result = await this.fetchOptions({ endpoint }, {
              [idKey]: item[idKey],
              [configIdKey]: item[configIdKey]
            });

            if (this.checkIsConditionsField(field)) {
              const component = PAYLOAD_TO_COMPONENT_TYPES_MAP[result.field_type]
              this[field].blocks[blockIndex].options[index][nextKey] = result.condition_list;
              this[field].blocks[blockIndex].components[index][nextKey] = component;
              this.$set(
                this[field].blocks[blockIndex].model[index],
                nextKey,
                clone(PAYLOAD_TO_COMPONENT_MODELS_MAP[result.field_type])
              );

              if (component === this.rangeComponentType) {
                this[field].blocks[blockIndex].model[index][nextKey][idKey] = result.condition_list[0][idKey];
                this[field].blocks[blockIndex].model[index][nextKey].range = ["", ""];
              }
            } else if (this.checkIsActionsField(field)) {
              const component = PAYLOAD_TO_COMPONENT_TYPES_MAP[result.field_type]
              this[field].options[index][nextKey] = result.action_list;
              this[field].components[index][nextKey] = component;
              this.$set(
                this[field].model[index],
                nextKey,
                clone(PAYLOAD_TO_COMPONENT_MODELS_MAP[result.field_type])
              );

              if (component === this.emailComponentType) {
                this[field].model[index][nextKey][idKey] = result.action_list[0][idKey];
                this[field].model[index][nextKey].text = "";
              }
            } else {
              this[field].options[index][nextKey] = result;
            }

            this.$set(this[field].memo, combinedValue, result);
          } else {
            if (this.checkIsConditionsField(field)) {
              this[field].blocks[blockIndex].options[index][nextKey] = this[field].memo[combinedValue];
            } else {
              this[field].options[index][nextKey] = this[field].memo[combinedValue];
            }
          }
        }
      } catch (err) {
        console.error("err", err);
      } finally {
        this.isLoading = false;
      }
    },
  },
}
